import { Component, ElementRef, Inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import type { IAssertionDialogData } from '../../interfaces/add-assertion.interface';
import { SHARE_TYPES } from '../../constants/share-type.data';
import { IShareType } from '../../interfaces/share-type.interface';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { ProjectView } from '../../models/project-view.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { CatalogService } from '../../../core/services/catalog.service';
import { UserPermissionService } from '../../../core/services/user-permissions.service';
import { ClaimsService } from '../../../core/services/claims.service';
import { Project } from '../../models/project.mode';
import { UserIndustries } from '../../models/user-industries.model';
import { ProjectStatus } from '../../models/project-status.model';
import { map, Observable, Subject } from 'rxjs';
import { TreeViewComponent } from '../tree-selector/tree-view.component';
import type { ITreeOptions } from '@odymaui/angular-tree-component';
import { UserGeography } from '../../models/user-geography.model';
import { TreeNodeService } from '../../../core/services/tree-node.service';
import { GEOGRAPHY_LABEL } from '../../constants/geography-selector.constants';
import { ADD_VIEW_ASSERTION_DIALOG_CONSTANTS, ASSERTIONS_MICRO_UI, CLASS_NAME } from '../../constants/claims-ui-constants';
import { Product } from '../../models/product.model';
import { ProductSelectorScrollService } from '../../../core/services/product-selector-scroll.service';
import { PRODUCT_SELECTOR_CONSTANT } from '../../constants/selector.constant';
import { ProductTreeService } from '../../../core/services/product-tree.service';
import { AuthorizationService } from '../../../core/services/authorization.service';
import { Geography } from '../../models/geography.model';
import { CLAIM_CALCULATION_TYPES } from '../../constants/claim-calculation.constants';
import { IClaimCalculationType } from '../../interfaces/claim-calculation.interface';
import { CompanySearchDialogComponent } from '../company-search/company-search-dialog.component';
import type { ICompanySearchDialogData } from '../../interfaces/company-search-dialog.interface';
import { BrandSearchDialogComponent } from '../brand-search/brand-search-dialog.component';
import type { IBrandSearchDialogData } from '../../interfaces/brand-search-dialog.interface';
import { ICalculatedBrandCompanyResponse } from '../../interfaces/calculated-brand-company';
import { ICalculatedBrandCompany } from '../../interfaces/claims.model';
import { takeUntil } from 'rxjs/operators';
import { SnackBarService } from '../../../core/services/snack-bar.service';
import { ICreateAssertionRequest } from '../../interfaces/create-assertion.interface';
import { TabOrderService } from '../../services/tab-order.service';


@Component({
  selector: 'emma-add-assertion-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    CdkDrag,
    CdkDragHandle,
    MatFormFieldModule,
    MatSelectModule,
    TreeViewComponent,
    NgSelectModule,
    FormsModule,

  ],
  templateUrl: './add-assertion-dialog.component.html',
  styleUrls: ['./add-assertion-dialog.component.scss'],
})
export class AddAssertionDialogComponent implements OnInit, OnDestroy {

  @ViewChild(TreeViewComponent) public treeView!: TreeViewComponent;
  @ViewChild('productselect', { static: true })
  public productselect!: MatSelect;
  @ViewChild('geographyselect', { static: true })
  public geographyselect!: MatSelect;
  public form!: FormGroup;
  public dialogTitle = 'Add Assertion';
  public isMinimizeState = true;
  public isViewMode = false;
  public componentName = 'AddAssertionDialog';
  public selectedGeography: any = null;
  public selectedGeographies: Array<any> = [];
  public selectedProducts: Array<any> = [];
  public userGeographies: Array<UserGeography> = [];
  public projectGeographies: Array<Geography> = [];
  public geographies: Array<any> = [];
  public isLoadingGeographies = false;
  public shareTypeOptions: Array<IShareType> = [];
  public companyTypes: Array<{ id: number; name: string }> = [];
  public claimCalculationOptions: Array<IClaimCalculationType> = [];
  public currentResearchProjects: Array<ProjectView> = [];
  public projectStatuses: Array<ProjectStatus> = [];
  public permissionIds: Array<number> = [];
  public projects: ReadonlyArray<Project> = [];
  public userIndustries: ReadonlyArray<UserIndustries> = [];
  public allProducts: Array<Product> = [];
  public productNodes: Array<any> = [];
  public selectedProduct: Array<Product> = [];
  public isSubmitting = false;
  public productSelectorLabel: string = PRODUCT_SELECTOR_CONSTANT.SELECT_A_PRODUCT_LABEL;
  public productsSignal = signal<Array<Product>>([]);
  public selectedProductsSignal = signal<Array<Product>>([]);
  private readonly scrollHandler = this.onScroll.bind(this);
  private readonly clickHandler = this.onScrollClick.bind(this);
  private readonly destroy$ = new Subject<void>();
  public geographiesError = signal<string | null>(null);
  public isCompanyAutoPopulated = false;
  public companyTypeOptions: Array<IShareType> = [];
  public selectedCompanyId: string | null = null;
  public selectedBrandId: string | null = null;
  public treeOptions: ITreeOptions = {
    useCheckbox: false,
    useTriState: false,
    useVirtualScroll: false,
    nodeHeight: 25,
    animateExpand: false,
    idField: 'id',
    displayField: 'name',
    childrenField: 'children',
    allowDrag: false,
    allowDrop: false,
    scrollOnActivate: false,
  };
  public claimYearOptions: Array<number> = [];
  public originalPrePopulatedCompany: string | null = null;
  public dataTypes: Array<{ id: number; name: string }> = [];
  @ViewChild('productTree') public productTree!: TreeViewComponent;
  @ViewChild('geographyTree') public geographyTree!: TreeViewComponent;
  public products: Array<Product> = [];
  public shareTypeId = '';
  public shareTypeError: string | null = null;
  public categoryError: string | null = null;
  public geographyError: string | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly catalogService: CatalogService,
    private readonly userPermissionService: UserPermissionService,
    private readonly claimsService: ClaimsService,
    public dialogRef: MatDialogRef<AddAssertionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IAssertionDialogData,
    private readonly treeNodeService: TreeNodeService,
    private readonly productTreeService: ProductTreeService,
    private readonly productSelectorScrollService: ProductSelectorScrollService,
    private readonly authorizationService: AuthorizationService,
    private readonly dialog: MatDialog,
    private readonly snackBarService: SnackBarService,
    private readonly elRef: ElementRef,
    private readonly tabOrderService: TabOrderService,
  ) {
    this.isViewMode = data.mode === 'view';
    this.dialogTitle = data.mode === ADD_VIEW_ASSERTION_DIALOG_CONSTANTS.MODES.VIEW ? ADD_VIEW_ASSERTION_DIALOG_CONSTANTS.DIALOG_TITLES.VIEW : ADD_VIEW_ASSERTION_DIALOG_CONSTANTS.DIALOG_TITLES.ADD;
  }


  private attachKeydownListener(): void {
    this.tabOrderService.attachKeydownListener(this.elRef, this.form, "assertions");
  }

  private detachKeydownListener(): void {
    this.tabOrderService.detachKeydownListener();
  }

  public ngOnDestroy(): void {
    this.detachKeydownListener();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private expandAllNodes(nodes: Array<any>): void {
    nodes.forEach(node => {
      if (node.children && node.children.length > 0) {
        node.expanded = true;
        this.expandAllNodes(node.children);
      }
    });
  }

  private normalizeToArray(value: any): Array<any> {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  public ngOnInit(): void {
    this.initializeForm();
    this.initializeClaimShareTypeOptions();
    this.initializeClaimCalculationOptions();
    this.loadUserGeographies();
    this.loadProjectStatuses().subscribe(() => {
      this.loadResearchProjects([4]);
    });
    this.initializeClaimYearOptions();
    this.attachKeydownListener();

    if (this.data.assertionData) {
      const assertion = { ...this.data.assertionData };
      if (assertion.year && !this.claimYearOptions.includes(assertion.year)) {
        this.claimYearOptions.push(assertion.year);
        this.claimYearOptions.sort((a, b) => b - a);
      }
      const projectCodeValue = this.isViewMode ? assertion.projectName : (assertion.projectCode || assertion.projectId?.toString());
      const dataTypeValue = this.isViewMode ? assertion.dataTypeName : (assertion.dataTypeCode || assertion.dataTypeName);

      this.form.patchValue({
        projectCode: projectCodeValue,
        dataType: dataTypeValue,
        claimShareType: assertion.shareCode,
        companyType: assertion.companyTypeName,
        claimCalculation: assertion.calculationCode ? Number(assertion.calculationCode) : null,
        claimRank: assertion.rank,
        company: assertion.companyName,
        claimYear: assertion.year,
        brand: assertion.brandName,
        notes: assertion.note
      });
      if (this.isViewMode && assertion.categoryName) {
        this.productSelectorLabel = assertion.categoryName;
      }

      if (assertion.geographyId && assertion.geographyName) {
        this.selectedGeographies = [{
          id: assertion.geographyId,
          geographyId: assertion.geographyId,
          name: assertion.geographyName
        }];
      }

      if (this.data.assertionData.company) {
        this.isCompanyAutoPopulated = false;
        this.originalPrePopulatedCompany = null;
      }
    }
    if (this.isViewMode) {
      this.form.disable();
    } else {
      this.form.get('projectCode')?.enable();
      this.form.get('claimShareType')?.disable();
      this.form.get('geography')?.disable();
      this.form.get('product')?.disable();
      this.form.get('dataType')?.disable();
      this.form.get('company')?.disable();
      this.form.get('brand')?.disable();
      this.form.get('companyType')?.disable();

      this.form.get('projectCode')?.valueChanges.subscribe(val => {
        if (val) {
          this.form.get('claimShareType')?.enable();
        }
      });


      this.form.get('claimShareType')?.valueChanges.subscribe(val => {
        if (val) {
          this.form.get('geography')?.enable();
          this.form.get('brand')?.setValue('', { emitEvent: false });
          this.selectedBrandId = null;
        }
      });


      this.form.get('geography')?.valueChanges.subscribe(val => {
        const hasGeography = this.normalizeToArray(val);
        if (!hasGeography.length) {
          this.geographyError = null;
          this.setGeographiesError();
          return;
        }
        const selectedGeographies = hasGeography
          .map(id => this.projectGeographies.find(g => g.geographyId === id));

        const allInvalid = selectedGeographies.length > 0 && selectedGeographies.every(g => !g);

        if (allInvalid) {
          this.form.get('product')?.disable();
          this.geographyError = ASSERTIONS_MICRO_UI.geographyError;
        } else {
          this.form.get('product')?.enable();
          this.geographyError = null;
        }

        this.setGeographiesError();
      });

      this.form.get('product')?.valueChanges.subscribe(val => {
        const hasProduct = this.normalizeToArray(val);
        if (!hasProduct.length) {
          this.categoryError = null;
          this.setGeographiesError();
          this.form.get('company')?.disable();
          this.form.get('brand')?.disable();
          return;
        }
        const selectedProducts = hasProduct
          .map(id => this.allProducts.find(product => product.id === id));
        const allInvalid = selectedProducts.length > 0 && selectedProducts.every(
          product => !product?.categoryGuid || !product.categoryGuid.trim()
        );
        this.categoryError = allInvalid
          ? ASSERTIONS_MICRO_UI.categoryError
          : null;

        this.setGeographiesError();
        this.form.get('dataType')?.enable();
        const shareTypeCode = this.getSelectedShareTypeCode();
        const companyControl = this.form.get('company');
        const brandControl = this.form.get('brand');
        if (!companyControl || !brandControl) return;
        companyControl.clearValidators();
        brandControl.clearValidators();
        companyControl.enable();
        brandControl.enable();
        if (ASSERTIONS_MICRO_UI.companyShareTypes.includes(shareTypeCode)) {
          companyControl.setValidators([Validators.required]);
          brandControl.disable();
        }

        if (ASSERTIONS_MICRO_UI.brandShareTypes.includes(shareTypeCode)) {
          brandControl.setValidators([Validators.required]);
          if (this.isCompanyAutoPopulated) {
            companyControl.disable();
            companyControl.setValidators([Validators.required]);
          } else {
            companyControl.disable();
            companyControl.clearValidators();
          }
        }
        companyControl.updateValueAndValidity();
        brandControl.updateValueAndValidity();
      });


      this.form.get('dataType')?.valueChanges.subscribe(val => {
        if (val) {
          this.form.get('companyType')?.enable();
        }
      });
    }
    this.setupProjectCodeListener();
    this.initializeAutoPopulation();
    this.preventSpacePropagation();
  }

  public preventSpacePropagation(): void {
    if (this.geographyselect) {
      this.geographyselect._handleKeydown = (event: KeyboardEvent) => {
        if (event.key === ' ') {
          event.stopPropagation();
        }
      };
    }
    if (this.productselect) {
      this.productselect._handleKeydown = (event: KeyboardEvent) => {
        if (event.key === ' ') {
          event.stopPropagation();
        }
      };
    }
  }

  private initializeAutoPopulation(): void {
    this.autoPopulateCompanyName();
  }

  private loadProjectStatuses(): Observable<void> {
    return this.catalogService.getProjectStatuses().pipe(
      map((statuses: ReadonlyArray<ProjectStatus>) => {
        this.projectStatuses = statuses
          .map((status) => ProjectStatus.fromAny(status))
          .filter((s): s is ProjectStatus => s !== null);
      }),
    );
  }

  private initializeClaimShareTypeOptions(): void {
    this.shareTypeOptions = SHARE_TYPES.map((type: IShareType) => ({
      code: type.code,
      label: type.label,
      id: type.id,
    }));
  }

  private initializeClaimCalculationOptions(): void {
    this.claimCalculationOptions = CLAIM_CALCULATION_TYPES.map(
      (type: IClaimCalculationType) => ({
        id: type.id,
        code: type.code,
        label: type.label,
      })
    );
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      projectCode: [null, Validators.required],
      dataType: [null as number | null, Validators.required],
      product: [[], Validators.required],
      claimShareType: [null, Validators.required],
      companyType: [null, Validators.required],
      geography: [[], Validators.required],
      claimCalculation: [null],
      category: [''],
      claimRank: [''],
      company: [''],
      claimYear: [null],
      brand: [''],
      notes: [''],
    });
  }

  private initializeClaimYearOptions(): void {
    const currentYear = new Date().getFullYear();
    this.claimYearOptions = [
      currentYear,
      currentYear - 1,
      currentYear - 2,
    ];
  }

  private setupProjectCodeListener(): void {
    this.form.get('projectCode')?.valueChanges.subscribe((projectCode: string) => {
      if (projectCode) {
        this.onProjectSelected(projectCode);
      }
    });
  }

  private onProjectSelected(projectCode: string): void {
    if (!projectCode) {
      this.geographies = [];
      return;
    }
    this.resetForm(projectCode);
    this.geographyError = this.shareTypeError = this.categoryError = null;
    this.setGeographiesError();
    this.loadCompanyTypes(projectCode);
    this.loadProjectGeographies(projectCode);
    this.loadProjectProducts(projectCode);
    this.loadDataTypes(projectCode);
  }

  public resetForm(projectCode: string) {
    const resetObj: any = { projectCode: projectCode };
    if (this.isCompanyAutoPopulated) {
      const companyValue = this.form.get('company')?.value;
      resetObj.company = companyValue;
    }
    this.form.reset(resetObj, { emitEvent: false });
    this.form.patchValue({
      geography: [],
      product: [],
      company: this.isCompanyAutoPopulated ? this.form.get('company')?.value : null,
      brand: null
    }, { emitEvent: false });
    this.productSelectorLabel = PRODUCT_SELECTOR_CONSTANT.SELECT_A_PRODUCT_LABEL;
    this.selectedGeographies = [];
    this.selectedProducts = [];
    this.selectedProduct = [];
    this.selectedCompanyId = null;
    this.selectedBrandId = null;
    this.geographyTree?.clearSelection();
    this.productTree?.clearSelection();
    const geoControl = this.form.get('geography');
    if (geoControl) {
      geoControl.setValue([], { emitEvent: false });
      geoControl.markAsUntouched();
      geoControl.markAsPristine();
      geoControl.updateValueAndValidity();
    }
    const prodControl = this.form.get('product');
    if (prodControl) {
      prodControl.setValue([], { emitEvent: false });
      prodControl.markAsUntouched();
      prodControl.markAsPristine();
      prodControl.updateValueAndValidity();
    }
  }

  public onClaimRankInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '');
    this.form.get('claimRank')?.setValue(input.value, { emitEvent: false });
  }

  private loadUserGeographies(): void {
    this.geographyError = null;
    this.setGeographiesError();
    this.authorizationService.getGeographies().subscribe({
      next: (geographies: Array<UserGeography>) => {
        this.userGeographies = geographies;
        if (this.projectGeographies.length > 0) {
          this.buildGeographyTree();
        }
      },
      error: () => {
        this.geographyError = 'Failed to load geographies';
        this.setGeographiesError(); 
        this.userGeographies = [];
        this.geographies = [];
      }
    });
  }

  private loadCompanyTypes(projectCode: string): void {
    this.catalogService.getShareCompanyTypes(projectCode).subscribe({
      next: (response): void => {
        this.companyTypes = response.map((companyType): { id: number; name: string } => ({
          id: companyType.id,
          name: companyType.name,
        }));
      },
      error: (): void => {
        this.companyTypes = [];
        this.shareTypeError = ASSERTIONS_MICRO_UI.shareTypeError;
        this.setGeographiesError();
      },
    });
  }

  private loadDataTypes(projectCode: string): void {
    if (!projectCode) {
      this.dataTypes = [];
      return;
    }
    this.catalogService.getDataTypes(projectCode).subscribe({
      next: (type: Array<{ id: number; name: string }>) => {
        const safeTypes = Array.isArray(type) ? type : [];
        this.dataTypes = safeTypes.map((dataType): { id: number; name: string } => ({
          id: dataType.id,
          name: dataType.name,
        }));
      },
      error: () => {
        this.dataTypes = [];
      },
    });
  }

  private loadProjectGeographies(projectCode: string): void {
    this.isLoadingGeographies = true;
    this.claimsService.getProjectGeographies(projectCode).subscribe({
      next: (geographies: Array<Geography>) => {
        this.projectGeographies = geographies;
        this.buildGeographyTree();
        this.isLoadingGeographies = false;
      },
      error: () => {
        this.projectGeographies = [];
        this.geographies = [];
        this.isLoadingGeographies = false;
      }
    });
  }

  private buildGeographyTree(): void {
    if (this.userGeographies.length > 0 && this.projectGeographies.length > 0) {
      this.geographies = this.treeNodeService.mapGeographiesToTreeNodes(
        this.projectGeographies,
        this.userGeographies,
        []
      );
      this.expandAllNodes(this.geographies);
    } else {
      this.geographies = [];
    }
  }

  private autoPopulateCompanyName(): void {
    const crmAccountId = this.data?.claimData?.salesforceAccountId;

    if (!crmAccountId) {
      this.isCompanyAutoPopulated = false;
      return;
    }

    const companyFormControl = this.form.get('company');
    const existingCompanyValue = companyFormControl?.value;

    if (existingCompanyValue && !this.isCompanyAutoPopulated) {
      return;
    }

    this.claimsService.getCompanyNameByCrmAccountId(crmAccountId).subscribe({
      next: (companyName: string | null) => {
        if (companyName && companyFormControl) {

          if (!companyFormControl.value || this.isCompanyAutoPopulated) {
            companyFormControl.setValue(companyName, { emitEvent: false });
            this.isCompanyAutoPopulated = true;
            this.originalPrePopulatedCompany = companyName;
          }
        } else if (companyFormControl) {
          companyFormControl.reset();
          this.isCompanyAutoPopulated = false;
          this.originalPrePopulatedCompany = null;
        }
      },
      error: () => {
        this.isCompanyAutoPopulated = false;
      },
    });
  }

  public restoreState(): void {
    this.isMinimizeState = !this.isMinimizeState;
  }

  public onClose(): void {
    this.detachKeydownListener();
    this.dialogRef.close();
  }

  public onCancel(): void {
    this.detachKeydownListener();
    this.dialogRef.close();
  }

  public onSave(): void {
    if (this.isSubmitting) {
      return;
    }
    if (!this.form.valid) {
      return;
    }
    const formData = this.prepareFormData();
    this.submitAddAssertion(formData);
  }

  private submitAddAssertion(formData: any): void {
    this.isSubmitting = true;
    const claimIdRaw = this.data?.claimData?.claimId ?? this.data?.assertionData?.claimId;
    const claimId = Number(claimIdRaw);
    if (!claimId) {
      this.isSubmitting = false;

      this.snackBarService.showSnackBar(
        ASSERTIONS_MICRO_UI.assertionMissingClaim,
        4000,
        CLASS_NAME.errors
      );
      return;
    }
    const request = this.mapFormToCreateAssertionRequest(formData, claimId);
    this.claimsService
      .createAssertion(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.dialogRef.close({
            action: 'save',
            data: formData,
            apiResponse: response,
          });

          this.snackBarService.showSnackBar(
            ASSERTIONS_MICRO_UI.assertionAddSuccessMessage,
            3000,
            CLASS_NAME.success
          );
        },
        error: (error) => {
          this.isSubmitting = false;

          this.snackBarService.showSnackBar(
            error?.error || error?.error?.message,
            4000,
            CLASS_NAME.errors
          );
        },
      });
  }

  private mapFormToCreateAssertionRequest(data: any, claimId: number): ICreateAssertionRequest {
    return {
      claimAssertions: [
        {
          claimId,
          projectCode: data.projectCode ?? '',
          geographyIds: this.selectedGeographies.map(g => String(g.geographyId ?? g.id)),
          categoryIds: this.selectedProducts
            .map(p => p.categoryGuid)
            .filter((guid): guid is string => !!guid && guid.trim().length > 0)
            .map(guid => String(guid)),
          dataTypeId: data.dataType ? Number(data.dataType) : null,
          shareCompanyTypeCode: data.companyType ? Number(data.companyType) : null,
          companyId: this.selectedCompanyId ?? null,
          brandId: this.selectedBrandId ?? null,
          claimShareCode: data.claimShareType ? Number(data.claimShareType) : null,
          claimCalculationCode: data.claimCalculation ? Number(data.claimCalculation) : null,
          claimYear: data.claimYear ? Number(data.claimYear) : null,
          claimRank: data.claimRank ? Number(data.claimRank) : null,
          notes: data.notes ?? ''
        }
      ]
    };
  }

  private prepareFormData(): any {
    const data = { ...this.form.value };
    return data;
  }

  public getTooltip(fieldName: string): string {
    const value = this.form.get(fieldName)?.value;

    switch (fieldName) {
      case 'companyType':
        return this.companyTypes.find(opt => opt.id === value)?.name || '';
      case 'claimCalculation':
        return this.claimCalculationOptions.find(opt => opt.id === value)?.label || '';
      case 'dataType':
        return this.dataTypes.find(opt => opt.id === value)?.name || '';
      case 'claimShareType':
        return this.shareTypeOptions.find(opt => opt.id === value)?.label || '';
      default:
        return value || '';
    }
  }

  public getShareTypeLabel(): string {
    const value = this.form.get('claimShareType')?.value;
    const option = this.shareTypeOptions.find(opt => opt.id === value);
    return option?.label || '';
  }

  private getSelectedShareTypeCode(): string {
    const id = this.form.get('claimShareType')?.value;
    return this.shareTypeOptions.find(x => x.id === id)?.code ?? '';
  }


  public getCompanyTypeLabel(): string {
    const value = this.form.get('companyType')?.value;
    if (typeof value === 'string') {
      return value;
    }
    const option = this.companyTypes.find(opt => opt.id === value);
    return option?.name || '';
  }

  public getClaimCalculationLabel(): string {
    const value = this.form.get('claimCalculation')?.value;
    const option = this.claimCalculationOptions.find(opt => opt.id === value);
    return option?.label || '';
  }

  public getCompanyTooltip(): string {
    const companyValue: string = this.form.get('company')?.value;
    if (this.isCompanyAutoPopulated && companyValue) {
      return 'Suggestion based on CRM Account Click field to change';
    } else {
      return '';
    }
  }

  public getDataTypeLabel(): string {
    const value = this.form.get('dataType')?.value;
    if (typeof value === 'string') {
      return value;
    }
    const option = this.dataTypes.find(opt => opt.id === value);
    return option?.name || '';
  }

  public setGeography(geography: any): void {
    if (Array.isArray(geography)) {
      this.selectedGeographies = [...geography];
      const geographyIds = geography.map(g => g.geographyId || g.id);
      this.form.patchValue({ geography: geographyIds });
    } else {
      this.selectedGeographies = [geography];
      this.form.patchValue({ geography: [geography.geographyId || geography.id] });
    }
    this.form.get('geography')?.markAsTouched();
    this.form.get('geography')?.updateValueAndValidity();
    const matchedGeographies = this.selectedGeographies.map(selectedGeography =>
      this.projectGeographies.find(projectGeography =>
        projectGeography.geographyId === (selectedGeography.geographyId || selectedGeography.id)
      )
    );
    const invalid = matchedGeographies.some(projectGeography => !projectGeography);
    if (invalid) {
      this.geographyError = ASSERTIONS_MICRO_UI.geographyError;
      this.setGeographiesError();
    } else {
      this.geographyError = null;
      this.setGeographiesError();
    }
  }

  public getGeographyLabel(): string {
    const count = this.selectedGeographies?.length || 0;
    if (count === 0) {
      return 'Select Geography';
    } else if (count === 1) {
      return this.selectedGeographies[0]?.name;
    } else {
      return GEOGRAPHY_LABEL;
    }
  }

  public onSelectOpened(): void {
    setTimeout(() => {
      if (this.treeView) {
        this.treeView.focusSearchBar();
      }
    }, 200);
  }

  public closeMatSelect(): void {
    this.geographyselect.close();
    this.productselect.close();
  }

  public searchText(term: string, item: ProjectView) {
    return (
      item.name.toLowerCase().includes(term.toLowerCase()) ||
      item.status.toLowerCase().includes(term.toLowerCase())
    );
  }

  public loadResearchProjects(statuses: Array<number>): void {
    this.catalogService
      .getResearchProjects(statuses)
      .subscribe((projects: ReadonlyArray<Project>) => {
        this.userPermissionService
          .getMyIndustries()
          .subscribe((userIndustries: ReadonlyArray<UserIndustries>) => {
            this.projects = projects;
            this.userIndustries = userIndustries;
            this.currentResearchProjects = this.mapToProjectView(projects, userIndustries);
          });
      });
  }

  private checkAndOpenDialog(type: 'company' | 'brand'): void {
    this.shareTypeId = this.getShareTypeLabel();
    const projectCode = this.form.get('projectCode')?.value;
    const geographyCodes = this.selectedGeographies?.map(g => g.code) || [];
    const productIds = this.form.get('product')?.value;
    const prePopulatedCompany = this.originalPrePopulatedCompany || this.form.get('company')?.value || '';

    if (!projectCode || !this.shareTypeId || !geographyCodes.length || !productIds?.length) {
      return;
    }

    if (type === 'company') {
      this.form.get('brand')?.disable();
    } else {
      this.form.get('company')?.disable();
    }

    if (this.shareTypeId === 'LBN') {
      this.shareTypeId = 'NBN';
    }

    this.claimsService.getCalculatedBrandAndCompanies({
      projectCode,
      shareTypeId: this.shareTypeId,
      geographyCodes,
      productIds
    }).subscribe((result: ICalculatedBrandCompanyResponse) => {
      const results: Array<ICalculatedBrandCompany> = result?.results || [];

      const dialogRef =
        type === 'company'
          ? this.dialog.open(CompanySearchDialogComponent, {
            panelClass: 'company-search-dialog',
            disableClose: true,
            data: {
              companies: results.map((item: ICalculatedBrandCompany) => ({
                id: item.id,
                brandCompanyName: item.brandCompanyName,
                companyBrandId: item.companyBrandId,
              })),
              prePopulatedCompany,
            } as Partial<ICompanySearchDialogData>,
          })
          : this.dialog.open(BrandSearchDialogComponent, {
            panelClass: 'brand-search-dialog',
            disableClose: true,
            data: {
              brands: results.map((item: ICalculatedBrandCompany) => ({
                id: item.id,
                brandCompanyName: item.brandCompanyName,
                companyBrandId: item.companyBrandId,
              })),
            } as Partial<IBrandSearchDialogData>,
          });

      dialogRef.afterClosed().subscribe((res: any) => {
        if (type === 'company' && res?.company) {
          this.form.get('company')?.setValue(res.company.brandCompanyName);
          this.selectedCompanyId = res.company.companyBrandId;
          this.isCompanyAutoPopulated = false;
        }

        if (type === 'brand' && res?.brand) {
          this.form.get('brand')?.setValue(res.brand.brandCompanyName);
          this.selectedBrandId = res.brand.companyBrandId;
        }
      });
    });

  }

  public onCompanyFieldClick(): void {
    this.isCompanyAutoPopulated = false;
    if (this.form.get('company')?.value && this.isCompanyAutoPopulated) {
      this.originalPrePopulatedCompany = this.form.get('company')?.value;
    }
    this.checkAndOpenDialog('company');
  }

  public onBrandFieldClick(): void {
    this.checkAndOpenDialog('brand');
  }


  public getBrandTooltip(): string {
    const brandValue: string = this.form.get('brand')?.value;
    if (brandValue) {
      return 'Click to search brands';
    } else {
      return '';
    }
  }

  private mapToProjectView(
    projects: ReadonlyArray<Project>,
    userIndustries: ReadonlyArray<UserIndustries>,
  ): Array<ProjectView> {
    const filteredProjects = projects.filter((prj: Project) =>
      userIndustries
        .map((usrInd: UserIndustries) => usrInd.industryCode)
        .includes(prj.industryCode),
    );
    const latestPerIndustry = this.pickLatestEditionPerIndustry(filteredProjects);
    const sortedProjects = this.sortProjectsByStatusId(latestPerIndustry);
    return sortedProjects.map(
      (project) =>
        new ProjectView(
          project.name,
          project.code,
          this.capitalizeFirstLetter(
            this.capitalizeFirstLetter(
              this.projectStatuses.find((x) => x.projectStatusCode === Number(project.status))
                ?.projectStatusName ?? '',
            ),
          ),
        ),
    );
  }

  private sortProjectsByStatusId(projects: Array<Project>): Array<Project> {
    return projects.sort((a, b) => {
      if (a.status < b.status) {
        return -1;
      }
      if (a.status > b.status) {
        return 1;
      }
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      if (a.edition < b.edition) {
        return -1;
      }
      if (a.edition > b.edition) {
        return 1;
      }
      return 0;
    });
  }

  private capitalizeFirstLetter(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  private pickLatestEditionPerIndustry(projects: ReadonlyArray<Project>): Array<Project> {
    const latestByIndustry = new Map<string | number, Project>();
    for (const p of projects) {
      const curr = latestByIndustry.get(p.industryCode);
      if (!curr || p.edition > curr.edition) {
        latestByIndustry.set(p.industryCode, p);
      }
    }
    return Array.from(latestByIndustry.values());
  }

  private loadProjectProducts(projectCode: string): void {
    if (!projectCode) {
      this.products = [];
      this.productsSignal.set([]);
      return;
    }

    this.claimsService.getProjectProducts(projectCode).subscribe({
      next: (products: Array<Product>) => {
        this.products = products;
        this.productsSignal.set(products);
        this.populateProductData();
      },
      error: () => {
        this.products = [];
        this.productsSignal.set([]);
        this.shareTypeError = ASSERTIONS_MICRO_UI.shareTypeError;
        this.setGeographiesError();
      }
    });
  }

  public onProductSelectorOpenedChange(isOpen: boolean): void {
    if (isOpen) {
      this.onProductSelectorOpened();
    } else {
      this.onSelectClosed();
    }
  }

  public onProductSelectorOpened(): void {
    requestAnimationFrame(() => {
      const panel = this.productselect?.panel?.nativeElement;
      if (panel) {
        panel.addEventListener('scroll', this.scrollHandler);
        panel.addEventListener('mousedown', this.clickHandler);
      }
    });

    this.productSelectorScrollService.expandProductNodes(this.allProducts);
    this.productSelectorScrollService.dispatchEvent('', 'searchFocusEvent');
  }

  public onScroll(event: any): void {
    this.productSelectorScrollService.loadNodesOnScrollDown(
      event,
      this.allProducts
    );
  }

  public onScrollClick(event: any): void {
    event.preventDefault();
    setTimeout(() => {
      this.productSelectorScrollService.dispatchEvent('', 'searchFocusEvent');
    }, 10);
  }

  public onTreeSelectedItemChange(selectedItems: Array<any>): void {

    this.selectedProducts = [...selectedItems];

    if (selectedItems.length === 1) {
      this.productSelectorLabel = selectedItems[0].name;
    } else if (selectedItems.length > 1) {
      this.productSelectorLabel = PRODUCT_SELECTOR_CONSTANT.MULTIPLE_SELECTIONS;
    } else {
      this.productSelectorLabel = PRODUCT_SELECTOR_CONSTANT.SELECT_A_PRODUCT_LABEL;
    }

    const productIds = selectedItems.map((product) => product.id);
    this.form.patchValue({ product: productIds });
    this.form.get('product')?.markAsTouched();
    this.form.get('product')?.updateValueAndValidity();

  }

  private populateProductData(): void {
    const productsData = this.productsSignal();

    if (!productsData?.length) {
      return;
    }

    this.allProducts = productsData;

    this.populateSelectedProducts();
    this.mapProductTree();
    this.resolveSelectedProductIds();
  }

  private populateSelectedProducts(): void {
    const selected = this.data?.assertionData?.selectedProducts;
    if (!selected) {
      this.selectedProducts = [];
      return;
    }
    if (Array.isArray(selected)) {
      this.selectedProducts = this.mapSelectedProductsWithIds(selected);
    } else {
      this.selectedProducts = [];
    }
  }

  private mapSelectedProductsWithIds(selected: Array<any>): Array<any> {
    return selected.map((sel: any) => {
      const found = this.allProducts.find((p: Product) => p.name === sel.name);
      return found ? { ...sel, id: found.id, categoryGuid: found.categoryGuid } : sel;
    });
  }

  private mapProductTree(): void {
    this.productNodes = this.productTreeService.mapProductsToTreeNodes(
      this.allProducts,
      this.selectedProducts
    );

    this.productNodes.forEach(rootNode =>
      this.productTreeService.updateNodeAndChildren(rootNode, this.allProducts, true)
    );

    this.productSelectorScrollService.expandedNodeIds = [];
    this.products = this.productNodes;
  }

  private resolveSelectedProductIds(): void {
    const selectedProductIds = this.form.get('product')?.value;

    if (!Array.isArray(selectedProductIds) || selectedProductIds.length === 0) {
      this.selectedProduct = [];
      return;
    }
    this.selectedProduct = this.allProducts.filter((p: Product) =>
      selectedProductIds.includes(p.id)
    );
  }

  public onSelectClosed(): void {
    if (this.productselect?.panel?.nativeElement) {
      this.productselect.panel.nativeElement.removeEventListener(
        'scroll', this.scrollHandler);
    }
  }

  public get isCompanyRequiredForBrandShare(): boolean {
    const shareTypeCode = this.getSelectedShareTypeCode();
    return (
      ASSERTIONS_MICRO_UI.brandShareTypes.includes(shareTypeCode) && !this.isCompanyAutoPopulated
    );
  }

  public get isSaveDisabled(): boolean {
    const isFormInvalid = this.form.invalid;
    const hasGeographyError = !!this.geographiesError();
    const isCompanyRequired = this.isCompanyRequiredForBrandShare;
    return isFormInvalid || hasGeographyError || isCompanyRequired;
  }

  public setGeographiesError() {
    const errors = [];
    if (this.shareTypeError) errors.push(this.shareTypeError);
    if (this.categoryError) errors.push(this.categoryError);
    if (this.geographyError) errors.push(this.geographyError);
    this.geographiesError.set(errors.length ? errors.join('\n') : null);
  }
}